import React, { useState, useEffect } from "react";
import { EPlateCorner } from "../../Model/EPlateCorner";
import {
  DropCalibrationPoint,
  PlateType,
  PlateTypeCalibration,
  WellLimit,
} from "../../Model/PlateType";
import DropPositionTabView from "./PlateCalibrationViews/DropPositionTabView";
import GeneraltabView from "./PlateCalibrationViews/GeneralTabView";
import WellLimitstabView from "./PlateCalibrationViews/WellLimitsTabView";
import TabPanel from "./TabPanel";
import AccordionToggle from "../Plate/AccordionToggle";
import Collapse from "../Common/Collapse";
import Disabled from "../Controls/Disabled";
import ImagerManualControl from "../Imager/ImagerManualControl";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import {
  getModuleStateById,
  getModuleState,
} from "../../redux/reducers/ModuleStateReducer";
import ImagerUtil from "../../Utility/ImagerUtil";
import Modal from "../Common/Modal";
import WarningModalView from "./WarningModalView";
import MtiButton from "../Buttons/MtiButton";

enum EGeneralTabState {
  PLATE_SETTINGS,
  WELL_LIMITS,
}

type Props = {
  plateType: PlateType;
  style?: React.CSSProperties;
  imagerIdentifier: string;
  plateTypeCalibration: PlateTypeCalibration;
  setPlateTypeCalibration: (plateTypeCalibration: PlateTypeCalibration) => void;
  waypointNames: string[];
  wellLimitEnabled: boolean;
  setWellLimitEnabled: (wellLimitEnabled: boolean) => void;
};

const PlateTypeCalibrationView = ({
  plateType,
  style = {},
  imagerIdentifier,
  plateTypeCalibration,
  setPlateTypeCalibration,
  waypointNames,
  wellLimitEnabled,
  setWellLimitEnabled,
}: Props) => {
  const imagerState = useTypedSelector(
    (state) => getModuleStateById(state)[imagerIdentifier]?.imager
  );
  const suiteState = useTypedSelector(
    (state) => getModuleState(state, "Suite").suite
  );

  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  if (suiteState == null) {
    throw new Error("Suite state is null");
  }

  const imagerManualControlEnabled = !ImagerUtil.getIsOpticControlDisabled(
    imagerState,
    suiteState
  );

  const [plateCorner, setPlateCorner] = useState(EPlateCorner.TopLeft);
  const [dropNumber, setDropNumber] = useState(0);
  const [generalTabState, setGeneralState] = useState([true, false]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const tabHeaders = ["General", "Drop Positions"];

  const setDropCalibration = (dropCalibration: DropCalibrationPoint) => {
    let ptc = { ...plateTypeCalibration };
    ptc.calibrationPoints[dropNumber] = dropCalibration;
    setPlateTypeCalibration(ptc);
  };

  const setWellLimit = (wellLimit: WellLimit) => {
    setPlateTypeCalibration({
      ...plateTypeCalibration,
      wellLimit: wellLimit,
    });
  };

  const setAccoordionState = (idx: number) => {
    let state = [false, false];

    state[idx] = !generalTabState[idx];

    setGeneralState(state);
  };

  const OnClickTabHeader = (header: string) => {
    setSelectedTabIndex(tabHeaders.findIndex((x) => x === header));
  };

  return (
    <div style={{ overflow: "auto", height: "100%", ...style }}>
      <TabPanel
        id="PlateSetup"
        collapsible={false}
        style={{ minHeight: 308, height: "100%" }}
        activeTabStyle={{ padding: 0 }}
        onClickTab={OnClickTabHeader}
        defaultSelectedIndex={selectedTabIndex}
      >
        {{
          General: (
            <>
              <AccordionToggle
                collapsible={false}
                expanded={true}
                onClick={() =>
                  setAccoordionState(EGeneralTabState.PLATE_SETTINGS)
                }
              >
                Plate Settings
              </AccordionToggle>
              <Collapse expanded={true}>
                <Disabled disabled={false}>
                  <GeneraltabView
                    plateType={plateType}
                    plateTypeCalibration={plateTypeCalibration}
                    setPlateTypeCalibration={setPlateTypeCalibration}
                    waypointNames={waypointNames}
                  />
                </Disabled>
              </Collapse>
              <AccordionToggle
                collapsible={false}
                expanded={true}
                onClick={() => setAccoordionState(EGeneralTabState.WELL_LIMITS)}
              >
                Well Limits
              </AccordionToggle>
              <Collapse expanded={true}>
                <Disabled disabled={false}>
                  <WellLimitstabView
                    wellLimit={plateTypeCalibration.wellLimit}
                    setWellLimit={setWellLimit}
                    wellLimitEnabled={wellLimitEnabled}
                    setWellLimitEnabled={setWellLimitEnabled}
                  />
                </Disabled>
              </Collapse>
            </>
          ),
          "Drop Positions": (
            <>
              <DropPositionTabView
                imagerState={imagerState}
                disabled={
                  imagerState.plate == null ||
                  imagerState.plate.plateType.id !== plateType.id ||
                  !imagerManualControlEnabled
                }
                plateType={plateType}
                imagerIdentifier={imagerIdentifier}
                plateCorner={plateCorner}
                setPlateCorner={setPlateCorner}
                dropNumber={dropNumber}
                setDropNumber={setDropNumber}
                dropCalibration={
                  plateTypeCalibration.calibrationPoints[dropNumber]
                }
                setDropCalibration={setDropCalibration}
                style={{ margin: 7 }}
              />
              <ImagerManualControl
                wellLimitEnabled={wellLimitEnabled}
                imagerIdentifier={imagerIdentifier}
                style={{
                  overflow: "none",
                  height: "auto",
                }}
              />
            </>
          ),
        }}
      </TabPanel>
    </div>
  );
};

export default PlateTypeCalibrationView;
